import { create } from 'zustand';
import type { WasmWarning } from '@wasm/types';

/** Status of a file in the conversion queue */
export type FileStatus = 'pending' | 'validating' | 'valid' | 'invalid' | 'converting' | 'complete' | 'failed';

/** A file in the conversion queue */
export interface CubeFile {
  id: string;
  filename: string;
  content: string;
  size: number;
  status: FileStatus;
  error?: string;
  lutSize?: number | undefined;
  title?: string | undefined;
  /** Original File object for reading content */
  file: File;
  /** Conversion result XMP content */
  result?: {
    xmpContent: string;
    warnings: WasmWarning[];
  };
}

/** Conversion options (maps to CLI arguments) */
export interface ConversionOptions {
  name?: string;
  shortName?: string;
  sortName?: string;
  group?: string;
  description?: string;
  lutSize?: number;
  requiresRgbTables: boolean;
  showInPresets: boolean;
  showInQuickActions: boolean;
  allowLarge: boolean;
}

/** Result of converting a single file */
export interface ConversionResult {
  inputFile: string;
  outputFile: string;
  xmpContent: string;
  warnings: WasmWarning[];
  durationMs: number;
}

/** Status of a conversion job */
export type JobStatus = 'idle' | 'converting' | 'complete' | 'cancelled';

/** Conversion store state */
interface ConversionState {
  // Files
  files: CubeFile[];
  
  // Options
  options: ConversionOptions;
  
  // Job status
  jobStatus: JobStatus;
  currentFileIndex: number;
  
  // Results
  results: ConversionResult[];
  errors: Array<{ filename: string; error: string }>;
  
  // Actions
  addFiles: (files: CubeFile[]) => void;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  updateFileStatus: (id: string, status: FileStatus, error?: string, metadata?: { lutSize?: number | undefined; title?: string | undefined }) => void;
  
  setOptions: (options: Partial<ConversionOptions>) => void;
  resetOptions: () => void;
  
  startJob: () => void;
  completeJob: () => void;
  cancelJob: () => void;
  setCurrentFileIndex: (index: number) => void;
  
  addResult: (result: ConversionResult) => void;
  addError: (filename: string, error: string) => void;
  clearResults: () => void;
  
  setFileResult: (id: string, result: { xmpContent: string; warnings: WasmWarning[] }) => void;
  
  reset: () => void;
}

/** Default conversion options */
const DEFAULT_OPTIONS: ConversionOptions = {
  requiresRgbTables: false,
  showInPresets: true,
  showInQuickActions: false,
  allowLarge: false,
};

/** Initial state */
const initialState = {
  files: [],
  options: { ...DEFAULT_OPTIONS },
  jobStatus: 'idle' as JobStatus,
  currentFileIndex: 0,
  results: [],
  errors: [],
};

/** Zustand store for conversion state */
export const useConversionStore = create<ConversionState>((set) => ({
  ...initialState,

  addFiles: (newFiles) =>
    set((state) => ({
      files: [...state.files, ...newFiles],
    })),

  removeFile: (id) =>
    set((state) => ({
      files: state.files.filter((f) => f.id !== id),
    })),

  clearFiles: () =>
    set({
      files: [],
    }),

  updateFileStatus: (id, status, error, metadata) =>
    set((state) => ({
      files: state.files.map((f) => {
        if (f.id !== id) return f;

        const next: CubeFile = {
          ...f,
          status,
          lutSize: metadata?.lutSize ?? f.lutSize,
          title: metadata?.title ?? f.title,
        };

        if (error !== undefined) {
          next.error = error;
        } else if (status !== 'failed' && status !== 'invalid') {
          delete (next as { error?: string }).error;
        }

        return next;
      }),
    })),

  setOptions: (newOptions) =>
    set((state) => ({
      options: { ...state.options, ...newOptions },
    })),

  resetOptions: () =>
    set({
      options: { ...DEFAULT_OPTIONS },
    }),

  startJob: () =>
    set({
      jobStatus: 'converting',
      currentFileIndex: 0,
      results: [],
      errors: [],
    }),

  completeJob: () =>
    set({
      jobStatus: 'complete',
    }),

  cancelJob: () =>
    set({
      jobStatus: 'cancelled',
    }),

  setCurrentFileIndex: (index) =>
    set({
      currentFileIndex: index,
    }),

  addResult: (result) =>
    set((state) => ({
      results: [...state.results, result],
    })),

  addError: (filename, error) =>
    set((state) => ({
      errors: [...state.errors, { filename, error }],
    })),

  clearResults: () =>
    set({
      results: [],
      errors: [],
    }),

  setFileResult: (id, result) =>
    set((state) => ({
      files: state.files.map((f) =>
        f.id === id ? { ...f, result } : f
      ),
    })),

  reset: () =>
    set(initialState),
}));

// Selector hooks for common patterns
export const useFiles = () => useConversionStore((state) => state.files);
export const useOptions = () => useConversionStore((state) => state.options);
export const useJobStatus = () => useConversionStore((state) => state.jobStatus);
export const useResults = () => useConversionStore((state) => state.results);
export const useErrors = () => useConversionStore((state) => state.errors);
export const useProgress = () =>
  useConversionStore((state) => ({
    current: state.currentFileIndex,
    total: state.files.length,
    completed: state.results.length + state.errors.length,
  }));
